import type { MouseEventHandler, ReactNode } from 'react';
import { LINK_TEXT_CLASS } from '@/components/base/shared-classes';
import type { EffectKey } from '@/mocks/endpoint-map';

export type ButtonVariant = 'primary' | 'default' | 'danger' | 'link';
export type ButtonSize = 'sm' | 'md';

export interface ButtonProps {
  /** §10：所有可見 UI 都要配置。由呼叫端傳入，第一層不自行組 ID。 */
  uiId: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  type?: 'button' | 'submit';
  disabled?: boolean;
  /**
   * 用原生 React handler 簽章而不是 `() => void`：只想要無參數的呼叫端照樣能傳
   * `() => doSomething()`（TS 允許少收參數），但需要 `event.preventDefault()` 的
   * 呼叫端不會被卡住。Link 的 onClick 也用同一個原則。
   */
  onClick?: MouseEventHandler<HTMLButtonElement>;
  /**
   * FUNCTIONAL-CONTRACT.md:83-86（D-1）：動作元素的「觸發目標」在 React 側無法從 DOM 讀出
   * （onClick 呼叫的是 `DataSource` 方法不是 URL），所以必須把 `<op>:<resource>` 標在屬性上，
   * 由 `src/mocks/endpoint-map.ts` 翻譯回 `analysis/api-inventory.md` 的端點。
   *
   * **只有真的會呼叫 `useDataSource()` 方法的按鈕才可以傳**（criteria C5-j 假動作率 = 0）：
   * 只開 modal、只清 state、只跳提示的按鈕不得標，否則是硬塞按鈕對數字。
   * 不傳就不輸出屬性（`undefined` 讓 React 整個略過該 attribute）。
   */
  dataEffect?: EffectKey;
  /**
   * FUNCTIONAL-CONTRACT.md 的 D-3（FC-4）：會開啟對話框的動作元素必須自報
   * `data-modal-trigger="<該對話框的 data-ui-id>"`。
   * React 的對話框是條件渲染，唯讀 GET 抽不到，且「哪顆按鈕開哪個對話框」無法從 DOM 讀出，
   * 所以 `tests/react/extract-modals.spec.ts` **只認這個屬性**（且只點 visible + enabled 者）。
   * 同一顆按鈕可以同時帶 `dataEffect`。不傳就不輸出屬性。
   */
  dataModalTrigger?: string;
  children: ReactNode;
}

/**
 * 各 variant 對應的原系統 class（§7 第 3 級證據，【雛形值待實機校準】）：
 *
 *   primary → `.btn-primary`  css/style.css:608-612    背景／邊框 #637d4c、白字 → token --color-brand
 *   danger  → `.btn-danger`   css/style.css:813-817    背景／邊框 #ed5565、白字 → token --color-danger
 *   link    → `.btn-link`     bootstrap.css:3946-3950  無底色，文字用連結色
 *   default → `.btn-white`    css/style.css:887-891    白底 + 1px #e7eaec 邊框 + 繼承文字色
 *
 * default 為什麼對到 `.btn-white` 而不是 `.btn-default`：
 * INSPINIA 把 `.btn-default` 覆寫成實心灰 #c2c2c2 白字（css/style.css:731-735），theme.css 沒有
 * 這個灰的 token；而實際 JSP 用量是 btn-white 106 次、btn-default 13 次
 * （grep -ro 'btn-white' --include=*.jsp WebContent 統計），所以「次要按鈕」的原系統長相
 * 以 btn-white 為準，而且它剛好完全對得上既有 token（--color-surface／--color-border）。
 * 真正需要那顆灰按鈕時再開第五個 variant，不現在預留。
 */
const VARIANT_CLASS: Record<ButtonVariant, string> = {
  primary: 'border-brand bg-brand text-white hover:bg-brand/90',
  default: 'border-border bg-surface text-ink hover:bg-surface-subtle',
  danger: 'border-danger bg-danger text-white hover:bg-danger/90',
  link: `border-transparent bg-transparent ${LINK_TEXT_CLASS}`,
};

/**
 * md → `.btn`     bootstrap.css:3004-3010  padding 6px 12px、font-size 14px（= --text-sm）
 * sm → `.btn-sm`  bootstrap.css:3986-3991  padding 5px 10px、font-size 12px（= --text-xs）
 */
const SIZE_CLASS: Record<ButtonSize, string> = {
  md: 'px-3 py-[6px] text-sm',
  sm: 'px-[10px] py-[5px] text-xs',
};

/**
 * `.btn` 共同外觀（bootstrap.css:3004-3016）＋ INSPINIA 的圓角覆寫
 * （css/style.css:577-579 `.btn { border-radius: 3px }`）＋ disabled 的 opacity .65
 * （bootstrap.css:3048-3056）。
 */
const BASE_CLASS =
  'inline-block cursor-pointer whitespace-nowrap rounded-[3px] border text-center align-middle ' +
  'disabled:cursor-not-allowed disabled:opacity-[.65]';

/**
 * 基礎按鈕。不知道任何 route／Controller／業務資料名稱（§9.1）。
 *
 * ponytail: 直接用原生 `<button>` 與原生 `disabled` 屬性，不自己管 pressed／loading 狀態。
 * loading 態是第二／三層的事（要顯示什麼文字牽涉業務語意），這裡不預留 boolean prop。
 */
export function Button({
  uiId,
  variant = 'primary',
  size = 'md',
  type = 'button',
  disabled = false,
  onClick,
  dataEffect,
  dataModalTrigger,
  children,
}: ButtonProps) {
  return (
    <button
      type={type}
      data-ui-id={uiId}
      data-effect={dataEffect}
      data-modal-trigger={dataModalTrigger}
      className={`${BASE_CLASS} ${VARIANT_CLASS[variant]} ${SIZE_CLASS[size]}`}
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </button>
  );
}
